import { Button, Select } from 'semantic-ui-react';
import moment from 'moment';
import { Margin, usePDF } from 'react-to-pdf';
import { chPerm, scrollUp, searchSelectProps } from 'src/components/helpers';
import styled from '@emotion/styled';
import { guestData, renderAnswers } from 'src/components/helpers/renderFormAnswers';
import { weddingFormPersonQuestions } from '../newRequest/questions/weddingFormPersonQuestions';
import { weddingFormCoupleQuestions } from '../newRequest/questions/weddingFormCoupleQuestions';
import { getGuestGroup } from '../../guestDashboard/guestDashboardApiCalls';
import {
  isCaptchaRequired,
  useRecaptchaToken,
  RECAPTCHA_SECURITY_ERROR,
} from 'src/components/helpers/recaptchaUtils';
import WeddingRequestFacilitatorAnswersModal from './weddingRequestFacilitatorAnswers';
import SignatureCanvas from 'react-signature-canvas';
import { useEffect, useRef, useState } from 'react';
import { screenSizes } from 'src/assets/styles/Literals';
import WeddingRequestEditAnswers from './weddingRequestEditAnswers';
import Swal from 'sweetalert2';

const ResultsComp = styled.div`
  background-color: white;
  min-width: 80%;
  .info {
    padding: 40px;
    .userdata-container {
      margin: 25px 50px 20px 0;
      .userdata {
        margin: 0 10px 0 0;
      }
    }
  }
  h1 {
    padding: 20px 0;
  }
  .content {
    h2 {
      &.title {
        padding: 30px 0;
      }
    }
    .request {
      .answers-container {
        margin: 20px 0;
        .individual {
          display: flex;
          justify-content: space-between;
          .him,
          .her {
            width: 50%;
          }
        }
        h2 {
          padding: 10px 0 0 0;
          color: #2185d0;
          text-align: center;
        }
        .question {
          margin: 20px 0;
          p {
            margin: 10px 0;
            &.answer {
              font-style: italic;
            }
          }
        }
        .couple {
          .pastor-question {
            font-weight: bolder;
            font-size: 25px;
          }
        }
        .couple-signatures {
          display: flex;
          justify-content: space-between;
          .his-signature,
          .her-signature {
            width: 50%;
          }
        }
      }
    }
  }
  @media (max-width: ${screenSizes.tablet}) {
    .content {
      .request {
        .answers-container {
          .individual {
            flex-direction: column;
            justify-content: center;
            .him,
            .her {
              width: 100%;
            }
          }
        }
      }
    }
  }
`;

const WeddingRequestAnswers = (props) => {
  const {
    answers,
    set_showWeddingRequestForm,
    sending,
    editAnswerVars,
    allWeddingFacilitators,
    admin_editing,
    set_admin_editing,
    facilitator_editing,
    set_facilitator_editing,
    deleteRequest,
    role,
    getInfo,
  } = props;
  const [guestsGroup, set_guestsGroup] = useState({});
  const [inputs, set_inputs] = useState({});
  const [editCoupleInfo, set_editCoupleInfo] = useState(false);
  const getGroupCaptchaToken = useRecaptchaToken('group_login');
  const { toPDF, targetRef } = usePDF({
    method: 'save',
    page: { margin: Margin.MEDIUM },
    filename: 'respuestas-solicitud-matrimonio.pdf',
  });

  useEffect(() => {
    scrollUp();
    getGuestsInfo();
  }, []);

  const hisSign = useRef(null);
  const herSign = useRef(null);

  useEffect(() => {
    let hisSignContent = answers.malePersonalData?.signature;
    if (hisSignContent && hisSign.current) {
      hisSign.current.fromData([hisSignContent]);
      hisSign.current.off();
    }
    let herSignContent = answers.femalePersonalData?.signature;
    if (herSignContent && herSign.current) {
      herSign.current.fromData([herSignContent]);
      herSign.current.off();
    }
  }, [inputs]);

  const getGuestsInfo = async () => {
    const captchaRequired = isCaptchaRequired();
    const maleCaptcha = await getGroupCaptchaToken({ skip: !captchaRequired });
    if (captchaRequired && !maleCaptcha) {
      Swal.fire(RECAPTCHA_SECURITY_ERROR);
      return;
    }

    await getGuestGroup({
      identification: answers?.MaleGuest?.identification,
      phone: answers?.MaleGuest?.phone,
      ...(captchaRequired ? { captcha: maleCaptcha } : {}),
      setGuestGroup: (group) => set_guestsGroup((prev) => ({ ...prev, male: group?.groupMember })),
    });

    const femaleCaptcha = await getGroupCaptchaToken({ skip: !captchaRequired });
    if (captchaRequired && !femaleCaptcha) {
      Swal.fire(RECAPTCHA_SECURITY_ERROR);
      return;
    }

    await getGuestGroup({
      identification: answers?.FemaleGuest?.identification,
      phone: answers?.FemaleGuest?.phone,
      ...(captchaRequired ? { captcha: femaleCaptcha } : {}),
      setGuestGroup: (group) =>
        set_guestsGroup((prev) => ({ ...prev, female: group?.groupMember })),
    });
  };

  return (
    <ResultsComp>
      <div className="info">
        <Button
          primary
          compact
          disabled={sending}
          onClick={() => set_showWeddingRequestForm(null)}
          style={{ display: 'block', margin: '10px 0' }}
        >
          Ir Atrás
        </Button>
        <h1>Respuestas</h1>
        {!!deleteRequest && role === 'admin' && chPerm(['solicitudes_matrimonio_admin']) && (
          <div style={{ margin: '10px  0' }}>
            <Button disabled={sending} onClick={deleteRequest}>
              Eliminar solicitud
            </Button>
          </div>
        )}
        <Button onClick={toPDF}>Descargar formulario</Button>
        <div ref={targetRef} className="printable content">
          <div className="request">
            <h2 className="title">Formulario de solicitud de matrimonio</h2>
            <p>
              Fecha de llenado:{' '}
              <strong>{moment.tz(answers.createdAt, 'America/Bogota').format('DD-MM-YYYY')}</strong>
            </p>
            {role === 'admin' && chPerm(['solicitudes_matrimonio_admin']) && (
              <Button
                size="tiny"
                onClick={() => {
                  set_inputs({
                    facilitator: answers?.facilitator_guest_id,
                    pastor: answers?.pastor_guest_id,
                  });
                  set_admin_editing(!admin_editing);
                }}
              >
                {admin_editing ? 'Cancelar edición' : 'Editar facilitador y pastor'}
              </Button>
            )}
            <h2>Información del facilitador</h2>
            {!answers?.FacilitatorGuest?.name || admin_editing ? (
              <>
                {chPerm(['solicitudes_matrimonio_admin']) ? (
                  <div>
                    <Select
                      placeholder="Seleccionar facilitador"
                      id="facilitator"
                      {...searchSelectProps({
                        name: 'facilitator',
                        value: Number(inputs?.facilitator),
                        handler: (e, { value }) => set_inputs({ ...inputs, facilitator: value }),
                        options: allWeddingFacilitators
                          ?.filter((facilitator) => facilitator.role === 'facilitator')
                          ?.map((facilitator) => ({
                            key: Number(facilitator?.Guest?.id),
                            text: `${facilitator?.Guest?.name} ${facilitator?.Guest?.lastName}`,
                            value: Number(facilitator?.Guest?.id),
                          })),
                      })}
                    />
                    <Button
                      primary
                      disabled={sending}
                      onClick={() => editAnswerVars('facilitator', inputs?.facilitator)}
                    >
                      Asignar facilitador
                    </Button>
                  </div>
                ) : (
                  <p>No se ha asignado un facilitador</p>
                )}
              </>
            ) : (
              <>
                <p className="userdata">
                  <strong>
                    {answers?.FacilitatorGuest?.name} {answers?.FacilitatorGuest?.lastName}
                  </strong>
                  <br />
                  Teléfono: <strong>{answers?.FacilitatorGuest?.phone}</strong>
                </p>
                <p className="userdata">
                  Cita:{' '}
                  <strong>
                    {answers?.facilitatorObservations?.date
                      ? moment
                          .tz(answers?.facilitatorObservations?.date, 'America/Bogota')
                          .format('DD-MM-YYYY')
                      : 'Sin cita'}
                  </strong>
                  <br />
                  Aprobación del facilitador:{' '}
                  <strong>
                    {answers?.facilitatorObservations?.approved === undefined
                      ? 'Sin responder'
                      : answers?.facilitatorObservations?.approved === 'Aprobado'
                      ? answers?.facilitatorObservations?.approved
                      : 'No: ' + answers?.facilitatorObservations?.approved}
                  </strong>
                </p>

                {role === 'facilitator' && (
                  <WeddingRequestFacilitatorAnswersModal
                    facilitator_editing={facilitator_editing}
                    set_facilitator_editing={set_facilitator_editing}
                    answers={answers}
                    loading={sending}
                    editAnswerVars={editAnswerVars}
                    inputs={inputs}
                    set_inputs={set_inputs}
                  />
                )}
              </>
            )}
            <h2>Información del pastor</h2>
            {role === 'admin' && (!answers?.PastorGuest?.name || admin_editing) ? (
              <>
                {chPerm(['solicitudes_matrimonio_admin']) ? (
                  <div>
                    <Select
                      placeholder="Seleccionar pastor"
                      id="pastor"
                      {...searchSelectProps({
                        name: 'pastor',
                        value: Number(inputs?.pastor),
                        handler: (e, { value }) => set_inputs({ ...inputs, pastor: value }),
                        options: allWeddingFacilitators
                          ?.filter((facilitator) => facilitator.role === 'pastor')
                          ?.map((pastor) => ({
                            key: Number(pastor?.Guest?.id),
                            text: `${pastor?.Guest?.name} ${pastor?.Guest?.lastName}`,
                            value: Number(pastor?.Guest?.id),
                          })),
                      })}
                    />
                    <Button
                      primary
                      disabled={sending}
                      onClick={() => editAnswerVars('pastor', inputs?.pastor)}
                    >
                      Asignar pastor
                    </Button>
                  </div>
                ) : (
                  <p>No se ha asignado un pastor</p>
                )}
              </>
            ) : (
              <p className="userdata">
                <strong>
                  {answers?.PastorGuest?.name} {answers?.PastorGuest?.lastName}
                </strong>
                <br />
                {!!answers?.PastorGuest?.phone && (
                  <>
                    Teléfono: <strong>{answers?.PastorGuest?.phone}</strong>
                  </>
                )}
              </p>
            )}
            <div className="answers-container">
              <div className="individual">
                <div className="him">
                  <h2>Información de él</h2>
                  <div className="userdata-container">
                    {!!answers.MaleGuest?.id && guestData(answers.MaleGuest)}
                  </div>
                  <Button
                    onClick={() =>
                      window.open(
                        `/perfil/crecer?identification=${answers?.MaleGuest?.identification}&phone=${answers?.MaleGuest?.phone}`,
                        '_blank'
                      )
                    }
                  >
                    Ver perfil del discípulo
                  </Button>
                  <h2>Información de el líder del grupo</h2>
                  <p className="userdata">
                    <strong>
                      {guestsGroup?.male?.ChurchGroup?.maleLeader?.name}{' '}
                      {guestsGroup?.male?.ChurchGroup?.maleLeader?.lastName}
                    </strong>
                  </p>
                  <p className="userdata">
                    Teléfono: <strong>{guestsGroup?.male?.ChurchGroup?.maleLeader?.phone}</strong>
                  </p>
                  <p className="userdata">
                    Aprobación del líder del grupo:{' '}
                    <strong>
                      {answers?.maleLeaderApproved === null
                        ? 'Sin responder'
                        : answers?.maleLeaderApproved === 'Aprobado'
                        ? answers?.maleLeaderApproved
                        : 'No: ' + answers?.maleLeaderApproved}
                    </strong>
                  </p>
                  {renderAnswers(
                    weddingFormPersonQuestions(answers?.malePersonalData),
                    answers?.malePersonalData
                  )}
                </div>
                <div className="her">
                  <h2>Información de ella</h2>
                  <div className="userdata-container">
                    {!!answers.FemaleGuest?.id && guestData(answers.FemaleGuest)}
                  </div>
                  <Button
                    onClick={() =>
                      window.open(
                        `/perfil/crecer?identification=${answers?.FemaleGuest?.identification}&phone=${answers?.FemaleGuest?.phone}`,
                        '_blank'
                      )
                    }
                  >
                    Ver perfil del discípulo
                  </Button>
                  <h2>Información de la líder del grupo</h2>
                  <p className="userdata">
                    <strong>
                      {guestsGroup?.female?.ChurchGroup?.femaleLeader?.name}{' '}
                      {guestsGroup?.female?.ChurchGroup?.femaleLeader?.lastName}
                    </strong>
                  </p>
                  <p className="userdata">
                    Teléfono:{' '}
                    <strong>{guestsGroup?.female?.ChurchGroup?.femaleLeader?.phone}</strong>
                  </p>
                  <p className="userdata">
                    Aprobación de la líder del grupo:{' '}
                    <strong>
                      {answers?.femaleLeaderApproved === null
                        ? 'Sin responder'
                        : answers?.femaleLeaderApproved === 'Aprobado'
                        ? answers?.femaleLeaderApproved
                        : 'No: ' + answers?.femaleLeaderApproved}
                    </strong>
                  </p>
                  {renderAnswers(
                    weddingFormPersonQuestions(answers?.femalePersonalData),
                    answers?.femalePersonalData
                  )}
                </div>
              </div>
              {editCoupleInfo ? (
                <WeddingRequestEditAnswers
                  answers={answers}
                  set_inputs={set_inputs}
                  inputs={inputs}
                  set_editCoupleInfo={set_editCoupleInfo}
                  getInfo={getInfo}
                />
              ) : (
                <div className="couple">
                  <h2>Información de la pareja</h2>
                  <Button onClick={() => set_editCoupleInfo(true)}>Editar sección</Button>
                  {renderAnswers(
                    weddingFormCoupleQuestions(answers?.coupleQuestions),
                    answers?.coupleQuestions
                  )}
                </div>
              )}
              <div className="couple-signatures">
                <div className="his-signature">
                  <label style={{ display: 'block' }} htmlFor={'signature'}>
                    Firma de él
                  </label>
                  <SignatureCanvas
                    penColor="black"
                    ref={hisSign}
                    canvasProps={{ width: 300, height: 200 }}
                  />
                </div>
                <div className="her-signature">
                  <label style={{ display: 'block' }} htmlFor={'signature'}>
                    Firma de ella
                  </label>
                  <SignatureCanvas
                    penColor="black"
                    ref={herSign}
                    canvasProps={{ width: 300, height: 200 }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ResultsComp>
  );
};
export default WeddingRequestAnswers;
